import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, RefreshCw, OctagonX, Play } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

// 2026-11-01 — Setup G · EMA 9/21 Reversal (NIFTY weekly options) — PAPER forward test.
// Backend: ema921_core.py (rules, parity-tested vs the backtest) + ema921_live.py (runner)
// + ema921_api.py. Rules are locked; this page only watches and offers Kill / Resume / close.

const f = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d }));
const rs = (v) => (v == null ? "—" : `${v >= 0 ? "+" : "−"}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const col = (v) => (v == null ? undefined : v > 0 ? "var(--bull)" : v < 0 ? "var(--bear)" : undefined);
const tm = (iso) => (iso ? String(iso).slice(11, 16) : "—");
const STATE_COLOR = { CONTINUE: "var(--text-muted-2)", GO_CANDIDATE: "var(--bull)", KILLED_PF: "var(--bear)", KILLED_DD: "var(--bear)" };
const Chip = ({ text, color }) => (
  <span className="inline-block px-1.5 py-0.5 mr-1 mb-0.5 text-[9px] uppercase tracking-wider border rounded-sm font-mono-data"
        style={{ color, borderColor: color }}>{text}</span>
);

const Progress = ({ label, value, max, good, warnAt }) => {
  const pct = Math.max(0, Math.min(100, (100 * (value || 0)) / max));
  const c = good ? "var(--bull)" : warnAt != null && (value || 0) >= warnAt ? "var(--bear)" : "var(--text-muted-2)";
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-[10px] font-mono-data text-muted-2"><span>{label}</span><span>{f(value, 2)} / {f(max, 2)}</span></div>
      <div className="h-1.5 bg-white/5"><div className="h-1.5" style={{ width: `${pct}%`, background: c }} /></div>
    </div>
  );
};

// 2026-11-01 — NIFTY gamma map (backend nifty_gamma.py via the PCR job) — LOG ONLY.
const GammaCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/nifty-gamma/summary", { params: { days: 30 } }).then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return null;
  const l = d.latest || {};
  const Row = ({ label, b }) => (
    <tr className="border-b border-grid/40">
      <td className="px-2 py-1 text-muted-2">{label}</td><td className="px-2 py-1">{b?.n ?? 0}</td>
      <td className="px-2 py-1">{f(b?.avg_range_30, 3)}%</td><td className="px-2 py-1">{f(b?.avg_eff_30, 2)}</td>
      <td className="px-2 py-1">{f(b?.avg_range_60, 3)}%</td><td className="px-2 py-1">{f(b?.avg_eff_60, 2)}</td>
    </tr>
  );
  return (
    <div className="border border-grid p-3 space-y-2" data-testid="setupg-gamma-card">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">NIFTY gamma map · log only — gates nothing</div>
      <div className="text-[11px] font-mono-data">
        {l.flip_us != null ? (
          <>Latest {l.day} {l.time}: NIFTY {f(l.spot, 1)} · gamma flip {f(l.flip_us, 1)} ({f(l.spot_vs_flip_pct, 2)}%) ·{" "}
            <span style={{ color: l.regime_us === "positive" ? "var(--bull)" : "var(--bear)" }}>{l.regime_us} gamma</span>{" "}
            · walls: call {l.call_wall} / put {l.put_wall} / max {l.gamma_wall} · total {f(l.total_gex_cr, 1)} cr/1%</>
        ) : <span className="text-muted-3">No gamma snapshot yet (fills from the NIFTY PCR job every 5 min in market hours).</span>}
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Does the gamma state predict NIFTY&apos;s next 30 / 60 min? Range = high-low in %, efficiency = |move| ÷ range (1 = clean
        trend, 0 = chop). Positive gamma should show smaller ranges and lower efficiency if the idea holds here. {d.snapshots_scored} snapshots scored.
      </div>
      <table className="w-full text-[10px] font-mono-data">
        <thead className="text-muted-3 uppercase tracking-[0.15em]">
          <tr className="border-b border-grid">{["Bucket", "n", "Range 30m", "Eff 30m", "Range 60m", "Eff 60m"].map((h) => <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>)}</tr>
        </thead>
        <tbody>
          {Object.entries(d.by_regime_us || {}).map(([k, b]) => <Row key={k} label={`US convention: ${k}`} b={b} />)}
          {Object.entries(d.by_flip_distance || {}).map(([k, b]) => <Row key={k} label={k} b={b} />)}
          {Object.entries(d.by_total_gex || {}).map(([k, b]) => <Row key={k} label={`writers' total GEX ${k}`} b={b} />)}
        </tbody>
      </table>
      {Object.keys(d.setup_g_by_regime || {}).length > 0 && (
        <div className="text-[10px] font-mono-data text-muted-2">
          Setup G closed trades by regime: {Object.entries(d.setup_g_by_regime).map(([k, v]) => `${k} ${v.n} (${v.won} won, ${rs(v.net_per_lot)}/lot)`).join(" · ")}
        </div>
      )}
    </div>
  );
};

export default function SetupG() {
  const { isAdmin } = useAuth();
  const [st, setSt] = useState(null);
  const [sig, setSig] = useState([]);
  const [trades, setTrades] = useState([]);
  const [met, setMet] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/setups/ema921/status"), api.get("/setups/ema921/signals"),
        api.get("/setups/ema921/trades", { params: { days: 120 } }), api.get("/setups/ema921/metrics")]);
      setSt(a.data); setSig(b.data.signals || []); setTrades(c.data.trades || []); setMet(d.data);
    } catch { /* keep last */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 15000);
    return () => clearInterval(id);
  }, [load]);

  const act = async (path, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try { await api.post(path); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const ctx = st?.context || {};
  const cp = met?.checkpoints || {};
  return (
    <section className="space-y-4" data-testid="setup-g-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">Setup G · NIFTY weekly options</div>
          <h2 className="font-heading text-2xl flex items-center gap-2"><FlaskConical size={20} /> EMA 9/21 Reversal
            <Chip text="PAPER" color="#f5b041" />
            {st?.kill_all && <Chip text="KILLED" color="var(--bear)" />}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-2 py-1 border border-grid text-[10px] font-mono-data uppercase flex items-center gap-1"><RefreshCw size={11} /> Refresh</button>
          {isAdmin && (st?.kill_all
            ? <button disabled={busy} onClick={() => act("/setups/ema921/resume")} className="px-2 py-1 border text-[10px] font-mono-data uppercase flex items-center gap-1" style={{ color: "var(--bull)", borderColor: "var(--bull)" }}><Play size={11} /> Resume</button>
            : <button disabled={busy} onClick={() => act("/setups/ema921/kill", "Kill Setup G paper trading and square off its paper positions?")} className="px-2 py-1 border text-[10px] font-mono-data uppercase flex items-center gap-1" style={{ color: "var(--bear)", borderColor: "var(--bear)" }}><OctagonX size={11} /> Kill</button>)}
        </div>
      </div>

      <div className="border border-grid p-3 text-[11px] font-mono-data flex flex-wrap gap-x-6 gap-y-1">
        <span>Day open <b>{f(ctx.day_open, 1)}</b></span>
        <span>ATR14 prev <b>{f(ctx.atr_prev, 1)}</b> <span className="text-muted-3">({ctx.atr_days ?? "—"} days)</span></span>
        <span>VIX prev <b>{f(ctx.vix_prev)}</b> {ctx.vix_gate == null
          ? <Chip text="not computed yet" color="var(--text-muted-2)" />
          : <Chip text={ctx.vix_gate ? "gate open" : "gate closed"} color={ctx.vix_gate ? "var(--bull)" : "var(--bear)"} />}</span>
        <span>Expiry <b>{ctx.expiry || "—"}</b> · lot {ctx.lot_size ?? "—"} · {st?.settings?.lots ?? 3} lots/trade</span>
        <span className="text-muted-3">Spot {f(ctx.spot_last, 1)} @ {tm(ctx.spot_last_bar)} · {st?.bars_processed ?? 0} bars processed today</span>
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Rules (locked): EMA 9/21 cross on closed 5-min NIFTY bars, entered only against the day&apos;s open move (≤ −0.1 ATR) and against the
        60-min trend, VIX prev ≥ 12, signals 09:30–14:30, ≤ 3 entries/day, day loss cap ₹3,000/lot. Buy ITM-5 (primary) / ATM, −30% stop,
        exit on the opposite cross or 15:00. Paper fills walk Kite&apos;s real order book; the backtest&apos;s model fill is stored beside it.
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {Object.entries(st?.variants || {}).map(([v, x]) => {
          const m = met?.variants?.[v] || {};
          const c = m.checkpoint || {};
          const b = m.benchmark || {};
          return (
            <div key={v} className="border border-grid p-3 space-y-2 font-mono-data text-[11px]" data-testid={`setup-g-variant-${v}`}
                 style={{ opacity: x.enabled ? 1 : 0.5 }}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold">{v}{v === st?.primary ? " · primary" : ""}</span>
                <Chip text={c.state || "CONTINUE"} color={STATE_COLOR[c.state] || "var(--text-muted-2)"} />
              </div>
              <div className="text-muted-2">Today: {x.entries_today}/3 entries · realised <span style={{ color: col(x.realised_today) }}>{rs(x.realised_today)}</span></div>
              {x.open ? (
                <div className="border border-grid/60 p-2 space-y-0.5">
                  <div><b>{x.open.dir}</b> NIFTY {x.open.strike} {x.open.opt_type} × {x.open.lots} lot · entry {f(x.open.entry?.fill)} · stop {f(x.open.stop_level)}</div>
                  <div>LTP {f(x.open.ltp)} · unrealised <span style={{ color: col(x.open.unrealised) }}>{rs(x.open.unrealised)}</span> · since {tm(x.open.entry?.time)}</div>
                  {isAdmin && <button disabled={busy} className="underline text-[10px]" onClick={() => act(`/setups/ema921/positions/${x.open.id}/close`, "Close this paper position now?")}>close (manual)</button>}
                </div>
              ) : <div className="text-muted-3">Flat</div>}
              <div className="pt-1 border-t border-grid/60 grid grid-cols-2 gap-x-3">
                <span>Trades <b>{m.trades ?? 0}</b> · win {f(m.win_rate_pct, 1)}%</span>
                <span>PF <b>{f(m.profit_factor)}</b> <span className="text-muted-3">(bt {f(b.profit_factor)})</span></span>
                <span>Net <b style={{ color: col(m.net) }}>{rs(m.net)}</b></span>
                <span>Per lot {rs(m.net_per_lot)} · DD {rs(m.max_dd_per_lot)}/lot</span>
                <span>Avg spread {f(m.avg_spread)}</span>
                <span>Fill vs model {m.fill_gap_per_lot == null ? "—" : `₹${f(m.fill_gap_per_lot, 0)}/lot/trade`}</span>
                <span className="col-span-2 text-muted-3">Top-5 trades = {m.top5_share_pct == null ? "—" : `${m.top5_share_pct}%`} of net · fills {Object.entries(m.fill_quality || {}).map(([k, n]) => `${k} ${n}`).join(", ") || "—"}</span>
              </div>
              <div className="space-y-1 pt-1">
                <Progress label={`Trades → checkpoints ${cp.early_kill_min_trades}/${cp.early_go_min_trades}/${cp.final_trades}`} value={m.trades || 0} max={cp.final_trades || 40} />
                <Progress label={`PF (kill < ${cp.early_kill_pf} at 15 · go ≥ ${cp.early_go_pf} at 25)`} value={m.profit_factor || 0} max={2} good={(m.profit_factor || 0) >= (cp.early_go_pf || 1.5)} />
                <Progress label={`Drawdown per lot (kill at ₹${((cp.kill_dd_per_lot || 30000) / 1000).toFixed(0)}k)`} value={-(m.max_dd_per_lot || 0)} max={cp.kill_dd_per_lot || 30000} warnAt={(cp.kill_dd_per_lot || 30000) * 0.7} />
                <div className="text-[10px] text-muted-3">{c.why}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-grid">
        <div className="px-3 py-2 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Signals today · {sig.length}</div>
        <div className="max-h-[320px] overflow-y-auto">
          <table className="w-full text-[11px] font-mono-data">
            <thead className="sticky top-0" style={{ background: "var(--surface)" }}>
              <tr className="text-muted-3 text-[9px] uppercase tracking-[0.15em] border-b border-grid">
                {["Time", "Dir", "#", "Spot", "vs open (ATR)", "60m", "VIX prev", "ITM5_BUY", "ATM_BUY"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {sig.length === 0 && <tr><td colSpan={9} className="px-2 py-3 text-muted-3">No crosses yet today.</td></tr>}
              {sig.map((s) => (
                <tr key={s.id} className="border-b border-grid/40">
                  <td className="px-2 py-1">{tm(s.signal_time)}{s.late ? " (late)" : ""}</td>
                  <td className="px-2 py-1" style={{ color: s.dir > 0 ? "var(--bull)" : "var(--bear)" }}>{s.dir > 0 ? "LONG" : "SHORT"}</td>
                  <td className="px-2 py-1">{s.cross_no}</td>
                  <td className="px-2 py-1">{f(s.spot_close, 1)}</td>
                  <td className="px-2 py-1">{f(s.move_open_atr, 2)}</td>
                  <td className="px-2 py-1">{s.h60_sign == null ? "—" : s.h60_sign > 0 ? "up" : s.h60_sign < 0 ? "down" : "flat"}</td>
                  <td className="px-2 py-1">{f(s.vix_prev)}</td>
                  {["ITM5_BUY", "ATM_BUY"].map((v) => {
                    const d = (s.decisions || {})[v] || {};
                    return (
                      <td key={v} className="px-2 py-1">
                        <Chip text={d.decision || "—"} color={/ENTER/.test(d.decision || "") ? "var(--bull)" : d.decision === "EXIT" ? "#f5b041" : "var(--text-muted-2)"} />
                        {(d.reject_codes || []).map((c) => <Chip key={c} text={c} color="var(--bear)" />)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-grid">
        <div className="px-3 py-2 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Paper trades · {trades.length}</div>
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-[11px] font-mono-data">
            <thead className="sticky top-0" style={{ background: "var(--surface)" }}>
              <tr className="text-muted-3 text-[9px] uppercase tracking-[0.15em] border-b border-grid">
                {["Date", "Variant", "Dir", "Contract", "Lots", "In", "Out", "Exit", "Spread in/out", "Net", "Model net"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 && <tr><td colSpan={11} className="px-2 py-3 text-muted-3">No paper trades yet.</td></tr>}
              {trades.map((t) => (
                <tr key={t.id} className="border-b border-grid/40">
                  <td className="px-2 py-1 text-muted-3">{t.date} {tm(t.entry?.time)}</td>
                  <td className="px-2 py-1">{t.variant}</td>
                  <td className="px-2 py-1">{t.dir}</td>
                  <td className="px-2 py-1">{t.strike} {t.opt_type}</td>
                  <td className="px-2 py-1">{t.lots}</td>
                  <td className="px-2 py-1">{f(t.entry?.fill)}</td>
                  <td className="px-2 py-1">{t.status === "OPEN" ? "open" : f(t.exit?.fill)}</td>
                  <td className="px-2 py-1 text-muted-2">{t.exit?.reason || "—"}{t.exit?.time ? ` ${tm(t.exit.time)}` : ""}</td>
                  <td className="px-2 py-1 text-muted-2">{f(t.entry?.spread)} / {f(t.exit?.spread)}</td>
                  <td className="px-2 py-1" style={{ color: col(t.net) }}>{t.status === "OPEN" ? "—" : rs(t.net)}</td>
                  <td className="px-2 py-1 text-muted-2">{rs(t.model_net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <GammaCard />
    </section>
  );
}
